import React, { CSSProperties, forwardRef, useRef } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Tooltip } from './Tooltip';

// Dev guard
const __DEV__ = typeof process !== 'undefined' ? process.env.NODE_ENV !== 'production' : true;
// Throttle missing-aria warnings to avoid console flood in dev
let __ICONBTN_MISSING_ARIA_WARN_COUNT = 0;
const __ICONBTN_MAX_ARIA_WARN = 3;

export interface IconButtonProps {
  icon: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  ariaLabel?: string;
  ariaLabelKey?: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'custom';
  customColorClasses?: {
    bg: string;
    text: string;
    hoverBg?: string;
    activeBg?: string;
  };
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  disabled?: boolean;
  tooltip?: string;
  tooltipKey?: string;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  badge?: number | string;
  badgeColor?: 'red' | 'green' | 'blue' | 'yellow' | 'neutral';
  applyNeobrutalistEffects?: boolean;
  type?: 'button' | 'submit' | 'reset';
  style?: CSSProperties;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(({
  icon,
  onClick,
  ariaLabel: directAriaLabel,
  ariaLabelKey,
  variant = 'secondary',
  customColorClasses,
  size = 'md',
  className,
  disabled = false,
  tooltip: directTooltip,
  tooltipKey,
  tooltipPosition = 'top',
  badge,
  badgeColor = 'red',
  applyNeobrutalistEffects = true,
  type = 'button',
  style,
}, ref) => {
  const { t } = useTranslation(['public_showcase', 'solution_showcase']);

  const resolvedAriaLabel = ariaLabelKey ? t(ariaLabelKey) : directAriaLabel;
  const resolvedTooltip = tooltipKey ? t(tooltipKey) : directTooltip;

  // Dev-only, throttled warning
  const warnedRef = useRef(false);
  if (__DEV__ && !resolvedAriaLabel && !warnedRef.current && __ICONBTN_MISSING_ARIA_WARN_COUNT < __ICONBTN_MAX_ARIA_WARN) {
    console.warn('IconButton is missing ariaLabel or ariaLabelKey. This is important for accessibility.');
    warnedRef.current = true;
    __ICONBTN_MISSING_ARIA_WARN_COUNT += 1;
  }

  // Base classes with dual-theme support
  const baseClasses = clsx(
    // Structure
    'relative font-medium flex items-center justify-center',
    'transition-all duration-200',
    'disabled:opacity-60 disabled:cursor-not-allowed',
    'focus:outline-none',

    // Neobrutalism base (default)
    'border-2 border-black rounded-xl',
    'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-500',

    // Neobrutalism effects (when enabled)
    applyNeobrutalistEffects && [
      'transform',
      'hover:-translate-x-1 hover:-translate-y-1',
      'hover:shadow-[4px_4px_0_0_rgb(17,24,39)]',
      'active:translate-x-0 active:translate-y-0 active:shadow-none',
      'disabled:hover:shadow-none disabled:hover:translate-x-0 disabled:hover:translate-y-0',
    ],

    // Liquid glass overrides
    '[.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20 [.theme-liquid-glass_&]:rounded-xl',
    '[.theme-liquid-glass_&]:focus-visible:ring-violet-400 [.theme-liquid-glass_&]:focus-visible:ring-offset-slate-900',
    applyNeobrutalistEffects && [
      '[.theme-liquid-glass_&]:hover:translate-x-0 [.theme-liquid-glass_&]:hover:-translate-y-0.5',
      '[.theme-liquid-glass_&]:hover:shadow-[0_8px_24px_rgba(139,92,246,0.25)]',
      '[.theme-liquid-glass_&]:active:translate-y-0 [.theme-liquid-glass_&]:active:shadow-[0_4px_16px_rgba(0,0,0,0.15)]',
    ],
  );

  const sizeClasses = {
    xs: 'w-7 h-7 p-1',
    sm: 'w-8 h-8 p-1.5',
    md: 'w-10 h-10 p-2',
    lg: 'w-12 h-12 p-2.5',
    xl: 'w-14 h-14 p-3',
  };

  // Variant classes with dual-theme support
  const variantClasses = {
    primary: clsx(
      // Neobrutalism
      'bg-emerald-500 text-white hover:bg-emerald-400 active:bg-emerald-600',
      // Liquid glass
      '[.theme-liquid-glass_&]:bg-violet-500/80 [.theme-liquid-glass_&]:hover:bg-violet-400/80 [.theme-liquid-glass_&]:active:bg-violet-600/80',
      '[.theme-liquid-glass_&]:backdrop-blur-sm',
    ),
    secondary: clsx(
      // Neobrutalism
      'bg-gray-100 text-gray-900 hover:bg-gray-50 active:bg-gray-200',
      // Liquid glass
      '[.theme-liquid-glass_&]:bg-white/10 [.theme-liquid-glass_&]:text-white',
      '[.theme-liquid-glass_&]:hover:bg-white/20 [.theme-liquid-glass_&]:active:bg-white/5',
    ),
    outline: clsx(
      // Neobrutalism
      'bg-white text-gray-900 hover:bg-gray-50 active:bg-gray-100',
      // Liquid glass
      '[.theme-liquid-glass_&]:bg-transparent [.theme-liquid-glass_&]:text-white',
      '[.theme-liquid-glass_&]:border-white/30',
      '[.theme-liquid-glass_&]:hover:bg-white/10 [.theme-liquid-glass_&]:active:bg-white/5',
    ),
    ghost: clsx(
      // Neobrutalism
      'bg-transparent text-gray-700 border-transparent hover:border-black hover:bg-gray-50 active:bg-gray-100',
      // Liquid glass
      '[.theme-liquid-glass_&]:text-white/80 [.theme-liquid-glass_&]:border-transparent',
      '[.theme-liquid-glass_&]:hover:border-transparent [.theme-liquid-glass_&]:hover:bg-white/10',
      '[.theme-liquid-glass_&]:active:bg-white/5',
    ),
    danger: clsx(
      // Neobrutalism
      'bg-red-500 text-white hover:bg-red-400 active:bg-red-600',
      // Liquid glass
      '[.theme-liquid-glass_&]:bg-rose-500/80',
      '[.theme-liquid-glass_&]:hover:bg-rose-400/80 [.theme-liquid-glass_&]:active:bg-rose-600/80',
    ),
    custom: `${customColorClasses?.bg || 'bg-transparent'} ${customColorClasses?.text || 'text-inherit'} ${customColorClasses?.hoverBg ? `hover:${customColorClasses.hoverBg}` : ''} ${customColorClasses?.activeBg ? `active:${customColorClasses.activeBg}` : ''}`,
  };

  const badgeBaseClasses = clsx(
    'absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center',
    'text-[10px] font-bold text-white rounded-full border border-white px-1 shadow-sm z-10'
  );

  const badgeColorClasses = {
    red: 'bg-red-500',
    green: 'bg-emerald-500',
    blue: 'bg-blue-500',
    yellow: 'bg-amber-500',
    neutral: 'bg-slate-500',
  } as const;

  const buttonContent = (
    <button
      ref={ref}
      type={type}
      style={style}
      onClick={onClick}
      disabled={disabled}
      aria-label={resolvedAriaLabel || 'icon button'}
      className={clsx(baseClasses, sizeClasses[size], variantClasses[variant], className)}
    >
      <span className="flex items-center justify-center">{icon}</span>
      {badge !== undefined && (
        <span className={clsx(badgeBaseClasses, badgeColorClasses[badgeColor])}>
          {typeof badge === 'number' && badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );

  if (resolvedTooltip) {
    return (
      <Tooltip text={resolvedTooltip} position={tooltipPosition} disabled={disabled}>
        {buttonContent}
      </Tooltip>
    );
  }
  return buttonContent;
});

IconButton.displayName = 'IconButton';
